import React from 'react';
import { motion } from 'framer-motion';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiTrendingUp } = FiIcons;

const OrganicSearch = ({ data }) => {
  const safeData = data || {
    traffic: 0, conversions: 0, techHealth: 0, backlinks: 0,
    keywords: [], trafficHistory: []
  };

  const seoMetrics = [
    { title: 'Total Traffic', value: safeData.traffic.toLocaleString(), trend: 'up' },
    { title: 'Conversions', value: safeData.conversions.toLocaleString(), trend: 'up' },
    { title: 'Tech Health Score', value: `${safeData.techHealth}/100`, trend: 'up' },
    { title: 'Backlinks', value: safeData.backlinks.toLocaleString(), trend: 'up' }
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-gray-800 mb-2">Organic Search (SEO)</h2>
        <p className="text-gray-600">Search engine optimization performance metrics</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {seoMetrics.map((metric, index) => (
          <motion.div
            key={metric.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-gray-600">{metric.title}</h3>
              <div className="flex items-center space-x-1 text-sm text-green-600">
                <SafeIcon icon={FiTrendingUp} className="text-xs" />
              </div>
            </div>
            <div className="text-2xl font-bold text-gray-800">{metric.value}</div>
          </motion.div>
        ))}
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
      >
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Organic Traffic Trends</h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={safeData.trafficHistory}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="month" stroke="#6b7280" />
            <YAxis stroke="#6b7280" />
            <Tooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }} />
            <Bar dataKey="organic" fill="#00A859" radius={[4, 4, 0, 0]} />
            <Bar dataKey="clicks" fill="#002B60" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
      >
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Top Keyword Rankings</h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4 font-medium text-gray-600">Keyword</th>
                <th className="text-center py-3 px-4 font-medium text-gray-600">Position</th>
                <th className="text-center py-3 px-4 font-medium text-gray-600">Change</th>
                <th className="text-right py-3 px-4 font-medium text-gray-600">Volume</th>
                <th className="text-center py-3 px-4 font-medium text-gray-600">Difficulty</th>
              </tr>
            </thead>
            <tbody>
              {safeData.keywords.map((keyword, index) => (
                <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-4 px-4 font-medium text-gray-800">{keyword.keyword}</td>
                  <td className="py-4 px-4 text-center">
                    <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold ${
                        keyword.position <= 3 ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {keyword.position}
                    </span>
                  </td>
                  <td className="py-4 px-4 text-center">{keyword.change}</td>
                  <td className="py-4 px-4 text-right text-gray-600">{keyword.searches}</td>
                  <td className="py-4 px-4 text-center">{keyword.difficulty}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </motion.div>
    </div>
  );
};

export default OrganicSearch;